<template>
  <div>
    <b-card>
      <h2 v-if="!readonly" v-translate>Search and Delete</h2>
      <b-row>
        <b-col cols="12" md="4">
          <div class="form-group">
            <label for="quote-name" v-translate>Quote Name</label>
            <input id="quote-name" v-model.lazy="filter.quoteName" type="text" class="mb-2 col-12 form-control">
          </div>
        </b-col>
        <b-col cols="12" md="4">
          <div class="form-group">
            <label for="quote-includes" v-translate>Includes</label>
            <b-form-select id="quote-includes" v-model="filter.quoteIncludes" :options="[{value: true, text: $gettext('Companies Only')}, {value: false, text: $gettext('Generic Only')}, {value: null, text: $gettext('Generic + Companies')}]"></b-form-select>
          </div>
        </b-col>
        <b-col cols="12" md="2">
          <div class="form-group">
            <label for="quote-area-from" v-translate>Area From</label>
            <b-form-select id="quote-area-from" v-model="filter.quoteAreaFrom" :options="this.areasSelect">
              <template #first>
                <option :value="null" v-translate>Select area</option>
              </template>
            </b-form-select>
          </div>
        </b-col>
        <b-col cols="12" md="2">
          <div class="form-group">
            <label for="quote-area-to" v-translate>Area To</label>
            <b-form-select id="quote-area-to" v-model="filter.quoteAreaTo" :options="this.areasSelect">
              <template #first>
                <option :value="null" v-translate>Select area</option>
              </template>
            </b-form-select>
          </div>
        </b-col>
        <b-col cols="12" md="4">
          <div class="form-group">
            <label for="quote-platform" v-translate>Platform</label>
            <b-form-select id="quote-platform" v-model="filter.quotePlatform" :options="this.platforms.map(x => {return {value: x.id, text: x.name}})"></b-form-select>
          </div>
        </b-col>
        <b-col cols="12" md="4">
          <div class="form-group">
            <label for="quote-shipping-type" v-translate>Shipping Type</label>
            <b-form-select id="quote-shipping-type" v-model="filter.quoteShippingType" :options="this.shippingTypes.map(x => {return {value: x.id, text: x.name}})"></b-form-select>
          </div>
        </b-col>
        <b-col cols="12" md="4">
          <div class="form-group">
            <label for="quote-size" v-translate>Size</label>
            <b-form-select id="quote-size" v-model="filter.quoteShippingSizeType" :options="this.sizeTypes.map(x => {return {value: x.id, text: x.name}})"></b-form-select>
          </div>
        </b-col>
        <b-col cols="12" md="4" v-if="!readonly">
          <div class="form-group">
            <label for="quote-size" v-translate>Imported Quotes CSV</label>
            <b-form-file
              v-model="fileImported"
              :placeholder="$gettext('Choose an Imported CSV or drop it here...')"
              :drop-placeholder="$gettext('Drop file here...')"
              @input="handleFileImportedSelect"
              ref="importedQuotes"
              id="importedQuotes"
              accept=".csv"
            ></b-form-file>
          </div>
        </b-col>
        <b-col cols="12" md="4">
          <div class="form-group">
            <label for="quote-size" v-translate>Company</label>
            <company-autocomplete ref="companyAc" @selected="onSearchCompanySelected" v-if="filter.quoteCompanies.length === 0" />
            <div v-for="(item, index) in filter.quoteCompanies" :key="index" v-else>
              {{ item.name }} <b-badge variant="danger" href="#" @click.prevent="removeCompanyFromFilters(index)">×</b-badge>
            </div>&nbsp;
          </div>
        </b-col>
        <b-col cols="12" md="4">
          <label for="quote-size">&nbsp;</label>
          <b-form-row>
            <b-col>
              <b-button size="sm" block variant="success" @click="searchQuotes" class="mb-2" :disabled="working">
                <b-spinner small v-if="working" />
                <translate>Search</translate>
              </b-button>
            </b-col>
            <b-col>
              <b-button size="sm" block variant="danger" @click="resetQuoteSearch" class="mb-2" :disabled="working">
                <translate>Reset Search</translate>
              </b-button>
            </b-col>
          </b-form-row>
        </b-col>
      </b-row>
      <div v-if="searchedQuotes.length && !readonly" class="my-3">
        <b-form-checkbox inline v-model="selectAllTrigger"><translate>Select/Unselect All</translate></b-form-checkbox>
        <b-button variant="primary" @click="backupSelected" :disabled="selected.length === 0 || deleting" v-translate>
          Backup Selected
        </b-button>
        <b-button variant="warning" v-b-modal.modalEditFormulas :disabled="!backuped || deleting" v-translate>
          EDIT Selected
        </b-button>
        <b-button variant="danger" @click="deleteSelected" :disabled="!backuped || deleting" v-translate>
          DELETE Selected
        </b-button>
      </div>
      <div v-if="searchedQuotes.length && readonly" class="my-3">
        <b-form-checkbox inline v-model="selectAllTrigger"><translate>Select/Unselect All</translate></b-form-checkbox>
        <b-button variant="warning" v-b-modal.modalEditFormulas v-translate>
          VIEW Selected
        </b-button>
      </div>
      <b-modal id="modalEditFormulas" size="xl" ref="modalEditFormulas" hide-footer @hidden="resetEditFormulas">
        <div slot="modal-title" v-translate>Bulk Edit Formulas</div>
        <div slot="modal-header-close"><img src="@/assets/img/ic_close.svg" alt="×" /></div>
        <b-row v-if="updating">
          <b-col>
            <b-progress :max="totalToUpdate.length" height="2rem" class="mb-3">
              <b-progress-bar :value="updatedCount">
                <span v-translate="{count: updatedCount, total: totalToUpdate.length}">Progress: <strong>%{count} / %{total}</strong></span>
              </b-progress-bar>
            </b-progress>
          </b-col>
        </b-row>
        <b-row v-if="!readonly">
          <b-col>
            <h4 v-translate>Search &amp; Replace in names</h4>
            <b-row>
              <b-col sm="4">
                <b-row>
                  <b-col sm="5">
                    <label for="search-for" v-translate>Search for:</label>
                  </b-col>
                  <b-col sm="7">
                    <b-form-input id="search-for" v-model="searchFor"></b-form-input>
                  </b-col>
                </b-row>
              </b-col>
              <b-col sm="4">
                <b-row>
                  <b-col sm="5">
                    <label for="replace-with" v-translate>Replace with:</label>
                  </b-col>
                  <b-col sm="7">
                    <b-form-input id="replace-with" v-model="replaceWith"></b-form-input>
                  </b-col>
                </b-row>
              </b-col>
              <b-col sm="4">
                <b-button @click="bulkReplaceInQuoteNames" :disabled="updating" v-translate>Replace</b-button>
              </b-col>
            </b-row>
            <hr>
          </b-col>
        </b-row>
        <b-row v-if="!readonly">
          <b-col cols="12">
            <h4 v-translate>Replace in Quotes</h4>
          </b-col>
          <b-col cols="12" md="4">
            <div class="form-group">
              <label for="quote-platform" v-translate>Platform</label>
              <b-form-select id="quote-platform" v-model="bulkReplaces.platform_id" :options="platformsSelect">
                <template #first>
                  <option :value="false" v-translate>*** NO CHANGES ***</option>
                  <option :value="null" v-translate>No specific platform</option>
                </template>
              </b-form-select>
            </div>
          </b-col>
          <b-col cols="12" md="4">
            <div class="form-group">
              <label for="quote-shipping-type" v-translate>Shipping Type</label>
              <b-form-select id="quote-shipping-type" v-model="bulkReplaces.shipping_type_id" :options="shippingTypesSelect">
                <template #first>
                  <option :value="false" v-translate>*** NO CHANGES ***</option>
                  <option :value="null" v-translate>No specific type</option>
                </template>
              </b-form-select>
            </div>
          </b-col>
          <b-col cols="12" md="4">
            <div class="form-group">
              <label for="quote-size" v-translate>Size</label>
              <b-form-select id="quote-size" v-model="bulkReplaces.size_id" :options="sizeTypesSelect">
                <template #first>
                  <option :value="false" v-translate>*** NO CHANGES ***</option>
                  <option :value="null" v-translate>No specific size</option>
                </template>
              </b-form-select>
            </div>
          </b-col>
          <b-col cols="12">
            <b-button @click="bulkReplaceInQuotes" :disabled="updating" v-translate>Replace</b-button>
            <hr>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <h4 v-translate>Formulas</h4>
            <b-row class="mb-3" v-if="!readonly">
              <b-col sm="5">
                <label for="formula-by-percentage" v-translate>Change All By Percentage</label>
              </b-col>
              <b-col sm="7">
                <b-input-group append="%">
                  <b-form-input id="formula-by-percentage" v-model="formulaByPercentage" :disabled="formulasIsNan"></b-form-input>
                </b-input-group>
                <b-form-checkbox :disabled="formulasIsNan" v-model="formulasAsIntegers"><translate>Force Integers</translate></b-form-checkbox>
              </b-col>
            </b-row>
            <b-row class="mb-1" v-for="(formula, i) in distinctFormulas" :key="'formula' + i">
              <b-col sm="5">
                <label :for="`formula-${i}`">{{ formula }}</label>
              </b-col>
              <b-col sm="7" v-if="!readonly">
                <b-form-input :id="`formula-${i}`" v-model="newFormulas[formula]"></b-form-input>
              </b-col>
            </b-row>
          </b-col>
          <b-col>
            <h4 v-translate>Courier Commission</h4>
            <b-row class="mb-3" v-if="!readonly">
              <b-col sm="5">
                <label for="formula-courier-commission-by-percentage" v-translate>Change All By Percentage</label>
              </b-col>
              <b-col sm="7">
                <b-input-group append="%">
                  <b-form-input id="formula-courier-commission-by-percentage" v-model="formulaCourierCommissionByPercentage" :disabled="formulasCourierCommissionIsNan"></b-form-input>
                </b-input-group>
                <b-form-checkbox :disabled="formulasCourierCommissionIsNan" v-model="formulasCourierCommissionAsIntegers"><translate>Force Integers</translate></b-form-checkbox>
                </b-col>
            </b-row>
            <b-row class="mb-1" v-for="(formula, i) in distinctFormulasCourierCommission" :key="'formula-courier-' + i">
              <b-col sm="5">
                <label :for="`formula-courier-${i}`">{{ formula }}</label>
              </b-col>
              <b-col sm="7" v-if="!readonly">
                <b-form-input :id="`formula-courier-${i}`" v-model="newFormulasCourierCommission[formula]"></b-form-input>
              </b-col>
            </b-row>
          </b-col>
          <b-col>
            <h4 v-translate>Courier Commission for Tours</h4>
            <b-row class="mb-3" v-if="!readonly">
              <b-col sm="5">
                <label for="formula-courier-commission-tour-by-percentage" v-translate>Change All By Percentage</label>
              </b-col>
              <b-col sm="7">
                <b-input-group append="%">
                  <b-form-input id="formula-courier-commission-tour-by-percentage" v-model="formulaCourierCommissionTourByPercentage" :disabled="formulasCourierCommissionTourIsNan"></b-form-input>
                </b-input-group>
                <b-form-checkbox :disabled="formulasCourierCommissionTourIsNan" v-model="formulasCourierCommissionTourAsIntegers"><translate>Force Integers</translate></b-form-checkbox>
              </b-col>
            </b-row>
            <b-row class="mb-1" v-for="(formula, i) in distinctFormulasCourierCommissionTour" :key="'formula-courier-tour-' + i">
              <b-col sm="5">
                <label :for="`formula-courier-tour-${i}`">{{ formula }}</label>
              </b-col>
              <b-col sm="7" v-if="!readonly">
                <b-form-input :id="`formula-courier-tour-${i}`" v-model="newFormulasCourierCommissionTour[formula]"></b-form-input>
              </b-col>
            </b-row>
          </b-col>
        </b-row>
        <b-button @click="bulkUpdateFormulas" :disabled="updating" v-if="!readonly" v-translate>Update</b-button>
      </b-modal>
      <b-alert variant="danger" :show="deleting">
        <span v-translate="{count: count, total: selected.length}">Deleting %{count}/%{total}</span>
      </b-alert>
      <p v-if="total" v-translate="{total: this.total}">Total: <strong>%{total}</strong></p>
      <b-table id="quotesTable" ref="quotesTable" @row-selected="rowSelected" :busy.sync="working" selectable stacked="md" foot-clone :items="searchedQuotes" :fields="quotesFields" v-if="searchedQuotes.length > 0">
        <div slot="table-busy" class="text-center text-danger my-2">
          <b-spinner class="align-middle"></b-spinner>
          <strong v-translate>Loading...</strong>
        </div>
        <template v-slot:cell(name)="data">
          <a :href="'/edit/quotes/' + data.item.id" target="_blank" v-if="!readonly">{{data.item.name}}</a>
          <span v-else>{{data.item.name}}</span>
        </template>
        <template v-slot:cell(size_id)="data">
          {{ data.item.size_id ? sizeTypes.findIndex(x => x.id === data.item.size_id) !== -1 ? sizeTypes.find(x => x.id === data.item.size_id).name : $gettext('ERROR') : $gettext('All') }}
        </template>
        <template v-slot:cell(area_id_from)="data">
          {{ data.item.area_id_from ? areas.findIndex(x => x.id === data.item.area_id_from) !== -1 ? areas.find(x => x.id === data.item.area_id_from).name : $gettext('ERROR') : $gettext('All') }}
        </template>
        <template v-slot:cell(area_id_to)="data">
          {{ data.item.area_id_to ? areas.findIndex(x => x.id === data.item.area_id_to) !== -1 ? areas.find(x => x.id === data.item.area_id_to).name : $gettext('ERROR') : $gettext('All') }}
        </template>
        <template v-slot:cell(platform_id)="data">
          {{ data.item.platform_id ? platforms.find(x => x.id === data.item.platform_id).name : $gettext('All') }}
        </template>
        <template v-slot:cell(shipping_type_id)="data">
          {{ data.item.shipping_type_id ? shippingTypes.find(x => x.id === data.item.shipping_type_id).name : $gettext('All') }}
        </template>
        <template v-slot:cell(company_id)="row">
          {{row.item.quote_companies.length > 0 ? row.item.quote_companies.length : 0}}
        </template>
      </b-table>
    </b-card>
    <b-card v-if="!readonly">
      <h2 v-translate>Import</h2>
      <b-form-file
        v-model="file"
        :placeholder="$gettext('Choose a file or drop it here...')"
        :drop-placeholder="$gettext('Drop file here...')"
        @input="handleFileSelect"
        ref="csvToImport"
        id="csvToImport"
        accept=".csv"
      ></b-form-file>
      <b-alert variant="success" :show="importing" class="mt-3">
        <span v-translate="{count: count, total: importData.length}">Importing %{count}/%{total}</span>
      </b-alert>
      <b-alert variant="info" :show="importData.length > 0 && errors.length === 0" class="mt-3">
        <h5 v-translate="{total: importData.length}">It seems there are no errors and you are about to bulk import %{total} quotes. Is it OK?</h5>
        <b-button variant="success" @click="bulkImport" :disabled="importing">
          <b-spinner small v-show="importing" />
          <translate>OK! Let's import them!</translate>
        </b-button>
        <b-button variant="danger" @click="file = null; importData = []" :disabled="importing" v-translate>
          NOPE! Cancel it!
        </b-button>
      </b-alert>
      <b-alert variant="danger" :show="errors.length > 0" class="mt-3">
        <h5 v-translate>It seems there are errors, check 'em out:</h5>
        <ul>
          <li v-for="(item, index) in errors" :key="index">
            {{ item.message }}{{item.row ? ' (row: ' + item.row + (item.index ? ' - col: ' + item.index : '') + ')' : ''}}
          </li>
        </ul>
        <p v-translate>Please, review and fix your CSV file and upload it again</p>
      </b-alert>
    </b-card>
  </div>
</template>
<script>
  import CompanyAutocomplete from '@/components/autocompletes/CompanyAutocomplete'
  import Papa from 'papaparse'

  export default {
    name: 'BulkQuotesList',
    components: {
      CompanyAutocomplete
    },
    props: {
      readonly: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        file: null,
        fileImported: null,
        importData: [],
        errors: [],
        quotesFields: [
          {key: 'name', label: this.$gettext('Name')},
          {key: 'area_id_from', label: this.$gettext('From')},
          {key: 'area_id_to', label: this.$gettext('To')},
          {key: 'quote_formula.formula', label: this.$gettext('Formula')},
          {key: 'platform_id', label: this.$gettext('Platform')},
          {key: 'shipping_type_id', label: this.$gettext('Type')},
          {key: 'size_id', label: this.$gettext('Size')},
          {key: 'company_id', label: this.$gettext('Companies Count')}
        ],
        filters: [],
        filter: {
          ids: [],
          quoteName: '',
          quoteIncludes: null,
          quoteAreaFrom: null,
          quoteAreaTo: null,
          quotePlatform: '',
          quoteShippingType: '',
          quoteShippingSizeType: '',
          quoteCompanies: []
        },
        working: false,
        searchedQuotes: [],
        companiesQuotes: [],
        companyAcOptions: {
          debounce: 500,
          placeholder: this.$gettext('Company Name, Business Name or TIN'),
          inputClass: 'form-control'
        },
        searchCompanyQuery: '',
        minCharacter: 3,
        areas: [],
        platforms: [],
        platformsSelect: [],
        shippingTypes: [],
        shippingTypesSelect: [],
        sizeTypes: [],
        sizeTypesSelect: [],
        selected: [],
        selectAllTrigger: false,
        backuped: false,
        importing: false,
        deleting: false,
        count: 0,
        batchUpdateChunk: 15,
        total: null,
        updating: false,
        totalToUpdate: 0,
        updatedCount: 0,
        formulaByPercentage: null,
        newFormulas: {},
        formulasAsIntegers: false,
        formulaCourierCommissionByPercentage: null,
        newFormulasCourierCommission: {},
        formulasCourierCommissionAsIntegers: false,
        formulaCourierCommissionTourByPercentage: null,
        newFormulasCourierCommissionTour: {},
        formulasCourierCommissionTourAsIntegers: false,
        searchFor: null,
        replaceWith: null,
        bulkReplaces: {
          platform_id: false,
          shipping_type_id: false,
          size_id: false
        }
      }
    },
    computed: {
      distinctFormulas() {
        return [...new Set(this.selected.map(row => row.quote_formula.formula))]
      },
      formulasIsNan() {
        return !!this.distinctFormulas.filter(x => isNaN(x))[0]
      },
      distinctFormulasCourierCommission() {
        return [...new Set(this.selected.map(row => row.quote_formula.formula_courier_commission))]
      },
      formulasCourierCommissionIsNan() {
        return !!this.distinctFormulasCourierCommission.filter(x => isNaN(x))[0]
      },
      distinctFormulasCourierCommissionTour() {
        return [...new Set(this.selected.map(row => row.quote_formula.formula_courier_commission_tour))]
      },
      formulasCourierCommissionTourIsNan() {
        return !!this.distinctFormulasCourierCommissionTour.filter(x => isNaN(x))[0]
      }
    },
    watch: {
      formulaByPercentage(val) {
        if (!isNaN(val)) {
          for (let i = 0; i < this.distinctFormulas.length; i++) {
            this.newFormulas[this.distinctFormulas[i]] = parseFloat(this.distinctFormulas[i]) + parseFloat(this.distinctFormulas[i]) * val / 100
          }
        } else {
          this.newFormulas = {}
        }
      },
      formulaCourierCommissionByPercentage(val) {
        if (!isNaN(val)) {
          for (let i = 0; i < this.distinctFormulasCourierCommission.length; i++) {
            this.newFormulasCourierCommission[this.distinctFormulasCourierCommission[i]] = parseFloat(this.distinctFormulasCourierCommission[i]) + parseFloat(this.distinctFormulasCourierCommission[i]) * val / 100
          }
        } else {
          this.newFormulasCourierCommission = {}
        }
      },
      formulaCourierCommissionTourByPercentage(val) {
        if (!isNaN(val)) {
          for (let i = 0; i < this.distinctFormulasCourierCommissionTour.length; i++) {
            this.distinctFormulasCourierCommissionTour[this.distinctFormulasCourierCommissionTour[i]] = parseFloat(this.distinctFormulasCourierCommissionTour[i]) + parseFloat(this.distinctFormulasCourierCommissionTour[i]) * val / 100
          }
        } else {
          this.distinctFormulasCourierCommissionTour = {}
        }
      },
      'filter.ids': {
        handler (val) {
          if (!this.resetting) {
            if (val !== '') {
              this.filters['quoteIds'] = [ 'id', 'in', val ]
            } else {
              this.filters['quoteIds'] = []
            }
          }
        },
        deep: true
      },
      'filter.quoteName': {
        handler (val) {
          if (!this.resetting) {
            if (val !== '') {
              this.filters['quoteName'] = [ 'name', 'ct', val ]
            } else {
              this.filters['quoteName'] = []
            }
          }
        },
        deep: true
      },
      'filter.quoteIncludes': {
        handler (val) {
          if (!this.resetting) {
            if (val === true) {
              this.filters['quoteIncludes'] = ['quote_companies.company_id', 'in', [''], 'true']
            } else {
              this.filters['quoteIncludes'] = []
            }
          }
        },
        deep: true
      },
      'filter.quoteAreaFrom': {
        handler (val) {
          if (!this.resetting) {
            if (val !== null) {
              this.filters['quoteAreaFrom'] = [ 'area_id_from', 'eq', val ]
            } else {
              this.filters['quoteAreaFrom'] = []
            }
          }
        },
        deep: true
      },
      'filter.quoteAreaTo': {
        handler (val) {
          if (!this.resetting) {
            if (val !== null) {
              this.filters['quoteAreaTo'] = [ 'area_id_to', 'eq', val ]
            } else {
              this.filters['quoteAreaTo'] = []
            }
          }
        },
        deep: true
      },
      'filter.quotePlatform': {
        handler (val) {
          if (!this.resetting) {
            if (val !== '') {
              this.filters['quotePlatform'] = [ 'platform_id', 'eq', val ]
            } else {
              this.filters['quotePlatform'] = []
            }
          }
        },
        deep: true
      },
      'filter.quoteShippingType': {
        handler (val) {
          if (!this.resetting) {
            if (val !== '') {
              this.filters['quoteShippingType'] = [ 'shipping_type_id', 'eq', val ]
            } else {
              this.filters['quoteShippingType'] = []
            }
          }
        },
        deep: true
      },
      'filter.quoteShippingSizeType': {
        handler (val) {
          if (!this.resetting) {
            if (val !== '') {
              this.filters['quoteShippingSizeType'] = [ 'size_id', 'eq', val ]
            } else {
              this.filters['quoteShippingSizeType'] = []
            }
          }
        },
        deep: true
      },
      'filter.quoteCompanies': {
        handler (val) {
          if (!this.resetting) {
            if (val.length > 0) {
              let companies = val.map(function (item) {
                return item.id
              })
              this.filters['quoteCompanies'] = [ 'quote_companies.company_id', 'in', companies ]
            } else {
              this.filters['quoteCompanies'] = []
            }
          }
        },
        deep: true
      },
      selectAllTrigger(val) {
        if (val === true) {
          this.selectAll()
        } else {
          this.unselectAll()
        }
      }
    },
    created() {
      this.getQuotesRelations()
    },
    methods: {
      resetEditFormulas() {
        this.newFormulas = {}
        this.newFormulasCourierCommission = {}
        this.newFormulasCourierCommissionTour = {}
        this.formulaByPercentage = null
        this.formulaCourierCommissionByPercentage = null
        this.formulaCourierCommissionTourByPercentage = null
        this.searchFor = null
        this.replaceWith = null
      },
      async bulkReplaceInQuoteNames() {
        this.updating = true
        this.totalToUpdate = this.selected.filter(x => x.name.indexOf(this.searchFor) !== -1)
        this.updatedCount = 0
        let toUpdate = []
        for (let i = 0; i < this.selected.length; i++) {
          let payload = {}
          if (this.selected[i].name.indexOf(this.searchFor) !== -1) {
            payload.name = this.selected[i].name.replace(this.searchFor, this.replaceWith)
          }
          if (Object.keys(payload).length !== 0) {
            if (toUpdate.length === this.batchUpdateChunk) {
              await Promise.all(toUpdate)
                .then(r => {
                  toUpdate = []
                  this.updatedCount = this.updatedCount + r.length
                })
            }
            toUpdate.push(this.$http.patch(this.$appConfig.api.baseUrl + 'quotes/' + this.selected[i].id, payload))
          }
        }
        if (toUpdate.length > 0) {
          await Promise.all(toUpdate)
            .then(r => {
              toUpdate = []
              this.updatedCount = this.updatedCount + r.length
            })
        }
        this.filter.quoteName = this.replaceWith
        this.updating = false
        this.searchQuotes()
        this.$refs.modalEditFormulas.hide()
      },
      async bulkReplaceInQuotes() {
        let payload = {}
        for (let i = 0; i < Object.keys(this.bulkReplaces).length; i++) {
          const key = Object.keys(this.bulkReplaces)[i]
          if (this.bulkReplaces[key] !== false) {
            payload[key] = this.bulkReplaces[key]
          }
        }
        if (Object.keys(payload).length > 0) {
          this.updating = true
          this.totalToUpdate = this.selected
          this.updatedCount = 0
          let toUpdate = []
          for (let i = 0; i < this.selected.length; i++) {
            if (toUpdate.length === this.batchUpdateChunk) {
              await Promise.all(toUpdate)
                .then(r => {
                  toUpdate = []
                  this.updatedCount = this.updatedCount + r.length
                })
            }
            toUpdate.push(this.$http.patch(this.$appConfig.api.baseUrl + 'quotes/' + this.selected[i].id, payload))
          }
          if (toUpdate.length > 0) {
            await Promise.all(toUpdate)
              .then(r => {
                toUpdate = []
                this.updatedCount = this.updatedCount + r.length
              })
          }
          this.updating = false
          this.searchQuotes()
          this.$refs.modalEditFormulas.hide()
        }
      },
      async bulkUpdateFormulas() {
        this.updating = true
        this.totalToUpdate = this.selected.filter(x => Object.keys(this.newFormulas).indexOf(x.quote_formula.formula) !== -1 || Object.keys(this.newFormulasCourierCommission).indexOf(x.quote_formula.formula_courier_commission) !== -1 || Object.keys(this.newFormulasCourierCommissionTour).indexOf(x.quote_formula.formula_courier_commission_tour) !== -1)
        this.updatedCount = 0
        let toUpdate = []
        for (let i = 0; i < this.selected.length; i++) {
          let payload = {}
          if (this.newFormulas[this.selected[i].quote_formula.formula]) {
            payload.formula = this.newFormulas[this.selected[i].quote_formula.formula]
            if (this.formulasAsIntegers && !isNaN(payload.formula)) {
              payload.formula = parseInt(payload.formula)
            }
          }
          if (this.newFormulasCourierCommission[this.selected[i].quote_formula.formula_courier_commission]) {
            payload.formula_courier_commission = this.newFormulasCourierCommission[this.selected[i].quote_formula.formula_courier_commission]
            if (this.formulasCourierCommissionAsIntegers && !isNaN(payload.formula_courier_commission)) {
              payload.formula_courier_commission = parseInt(payload.formula_courier_commission)
            }
          }
          if (this.newFormulasCourierCommissionTour[this.selected[i].quote_formula.formula_courier_commission_tour]) {
            payload.formula_courier_commission_tour = this.newFormulasCourierCommissionTour[this.selected[i].quote_formula.formula_courier_commission_tour]
            if (this.formulasCourierCommissionTourAsIntegers && !isNaN(payload.formula_courier_commission_tour)) {
              payload.formula_courier_commission_tour = parseInt(payload.formula_courier_commission_tour)
            }
          }
          if (Object.keys(payload).length !== 0) {
            if (toUpdate.length === this.batchUpdateChunk) {
              await Promise.all(toUpdate)
                .then(r => {
                  toUpdate = []
                  this.updatedCount = this.updatedCount + r.length
                })
            }
            toUpdate.push(this.$http.patch(this.$appConfig.api.baseUrl + 'quote-formulas/' + this.selected[i].quote_formula_id, payload))
          }
        }
        if (toUpdate.length > 0) {
          await Promise.all(toUpdate)
            .then(r => {
              toUpdate = []
              this.updatedCount = this.updatedCount + r.length
            })
        }
        this.updating = false
        this.searchQuotes()
        this.$refs.modalEditFormulas.hide()
      },
      handleFileImportedSelect(evt) {
        let file = this.fileImported
        if (file) {
          const _this = this

          Papa.parse(file, {
            header: true,
            dynamicTyping: true,
            complete: function (results) {
              _this.filter.ids = results.data.map(id => { return id.quote_id })
            }
          })
        }
      },
      handleFileSelect(evt) {
        let file = this.file
        if (file) {
          const _this = this

          Papa.parse(file, {
            header: true,
            dynamicTyping: true,
            complete: function (results) {
              _this.errors = []
              _this.importData = []
              if (results.errors.length === 0) {
                if (
                  results.meta.fields.indexOf('name') !== -1 &&
                  results.meta.fields.indexOf('area_id_from') !== -1 &&
                  results.meta.fields.indexOf('area_id_to') !== -1 &&
                  results.meta.fields.indexOf('size_id') !== -1 &&
                  results.meta.fields.indexOf('shipping_type_id') !== -1 &&
                  results.meta.fields.indexOf('platform_id') !== -1 &&
                  results.meta.fields.indexOf('type') !== -1 &&
                  results.meta.fields.indexOf('apply_surcharge') !== -1 &&
                  results.meta.fields.indexOf('formula') !== -1 &&
                  results.meta.fields.indexOf('formula_courier_commission') !== -1 &&
                  results.meta.fields.indexOf('formula_courier_commission_tour') !== -1 &&
                  results.meta.fields.indexOf('base_price') !== -1 &&
                  results.meta.fields.indexOf('min_price') !== -1 &&
                  results.meta.fields.indexOf('max_price') !== -1
                ) {
                  _this.importData = results.data
                  // console.log((_this.importData[1].companies + '').split(','))
                } else {
                  _this.errors = [
                    {
                      message: this.$gettext('There are one or more missing required data columns')
                    }
                  ]
                }
              } else {
                _this.errors = results.errors
                console.log(results.errors)
              }
            }
          })
        }
      },
      async bulkImport() {
        this.importing = true
        this.count = 0
        let count = 0
        let ids = []
        let createdAtFrom
        for (let i = 0; i < this.importData.length; i++) {
          let autodiscover = this.customValuesAutodiscover(this.importData[i].formula + this.importData[i].formula_courier_commission + this.importData[i].formula_courier_commission_tour)
          let cleanedUpData = JSON.parse(JSON.stringify(this.importData[i]).replace(/{([a-zA-Z]+):[^}]+}/g, '{$1}'))
          let quote = {...cleanedUpData, ...autodiscover}
          let paramsQuoteFormulaValues = {
            base_price: quote.base_price,
            min_price: quote.min_price,
            max_price: quote.max_price,
            values: quote.values || {}
          }

          await this.$http.post(this.$appConfig.api.baseUrl + 'quote-formula-values', paramsQuoteFormulaValues)
            .then(async response => {
              if (i === 0) {
                createdAtFrom = response.body.created_at
              }
              let paramsQuoteFormulas = {
                formula: quote.formula + '',
                formula_courier_commission: quote.formula_courier_commission + '',
                formula_courier_commission_tour: quote.formula_courier_commission_tour + '',
                parameters: quote.parameters || '',
                quote_formula_value_id: response.body.id
              }
              this.$http.post(this.$appConfig.api.baseUrl + 'quote-formulas', paramsQuoteFormulas)
                .then(response => {
                  let paramsQuotes = {
                    name: quote.name,
                    area_id_from: quote.area_id_from,
                    area_id_to: quote.area_id_to,
                    size_id: quote.size_id,
                    shipping_type_id: quote.shipping_type_id,
                    platform_id: quote.platform_id,
                    type: quote.type,
                    apply_surcharge: quote.apply_surcharge,
                    quote_formula_id: response.body.id
                  }
                  this.$http.post(this.$appConfig.api.baseUrl + 'quotes', paramsQuotes)
                    .then(response => {
                      let quoteCompanies = quote.companies ? (quote.companies + '').split(',') : null
                      ids.push({quote_id: response.body.id})
                      count++
                      this.count = count
                      if (!quoteCompanies) {
                        if (count === this.importData.length) {
                          let filename = 'imported_quotes__' + response.body.created_at + '.csv'
                          this.exportToCsv(filename, Papa.unparse(ids))
                          this.file = null
                          this.importData = []
                          this.importing = false
                          this.filters.createdAtFrom = ['created_at', 'gte', createdAtFrom]
                          this.filters.createdAtTo = ['created_at', 'lte', this.moment.utc(response.body.created_at).add(5, 'seconds').format('YYYY-MM-DD HH:mm:ss')]
                          this.searchQuotes()
                        }
                      } else {
                        let companyIds = quoteCompanies.map(x => { return parseInt(x) })
                        this.$http.post(this.$appConfig.api.baseUrl + 'quotes/' + response.body.id + '/companies', companyIds)
                          .then(response => {
                            if (count === this.importData.length) {
                              let filename = 'imported_quotes__' + response.body.created_at + '.csv'
                              this.exportToCsv(filename, Papa.unparse(ids))
                              this.file = null
                              this.importData = []
                              this.importing = false
                              this.filters.createdAtFrom = ['created_at', 'gte', createdAtFrom]
                              this.filters.createdAtTo = ['created_at', 'lte', this.moment.utc(response.body.created_at).add(5, 'seconds').format('YYYY-MM-DD HH:mm:ss')]
                              this.searchQuotes()
                            }
                          })
                      }
                    })
                })
            })
        }
      },
      customValuesAutodiscover(formulas) {
        const excludeValues = [
          'kms',
          'visits',
          'shippingPrice',
          'basePrice',
          'minPrice',
          'maxPrice'
        ]
        const parameters = [
          'kms',
          'visits',
          'shippingPrice'
        ]
        const regexp = /\{([^}]+)\}/g
        let valuesArray = []
        let paramsArray = []
        let match = regexp.exec(formulas)
        while (match !== null) {
          if (excludeValues.indexOf(match[1]) === -1) {
            valuesArray.push(match[1])
          }
          if (parameters.indexOf(match[1]) !== -1) {
            paramsArray.push(match[1])
          }
          match = regexp.exec(formulas)
        }
        valuesArray = Object.assign({}, ...valuesArray.map(x => {
          return JSON.parse(JSON.parse(JSON.stringify('{"' + x.replace(/\(/g, '{"').replace(/\)/g, '}').replace(/:/g, '":').replace(/,/g, ',"').replace(/,"\{/g, ',{') + '}')))
        }))
        console.log(valuesArray)
        return {
          values: valuesArray,
          parameters: JSON.stringify([...new Set(paramsArray)])
        }
      },
      getQuotesRelations() {
        this.fetchAreas()

        this.$http.get(this.$appConfig.api.baseUrl + 'platforms')
          .then(response => {
            this.platforms = response.body.data
            this.platformsSelect = this.platforms.map((item) => {
              return {
                value: item.id,
                text: item.name
              }
            })
            this.platforms.unshift({
              id: 'null',
              name: this.$gettext('No specific platform')
            })
            this.platforms.unshift({
              id: '',
              name: this.$gettext('All platforms')
            })
          })
        this.$http.get(this.$appConfig.api.baseUrl + 'shipping-types')
          .then(response => {
            this.shippingTypes = response.body.data
            this.shippingTypesSelect = this.shippingTypes.map((item) => {
              return {
                value: item.id,
                text: item.name
              }
            })
            this.shippingTypes.unshift({
              id: 'null',
              name: this.$gettext('No specific type')
            })
            this.shippingTypes.unshift({
              id: '',
              name: this.$gettext('All types')
            })
          })
        this.$http.get(this.$appConfig.api.baseUrl + 'sizes')
          .then(response => {
            this.sizeTypes = response.body.data
            this.sizeTypesSelect = this.sizeTypes.map((item) => {
              return {
                value: item.id,
                text: item.name
              }
            })
            this.sizeTypes.unshift({
              id: 'null',
              name: this.$gettext('No specific size')
            })
            this.sizeTypes.unshift({
              id: '',
              name: this.$gettext('All sizes')
            })
            for (let i = 0; i < this.sizeTypesSelect.length; i++) {
              for (let n = 0; n < this.companyFormulas.length; n++) {
                if (this.companyFormulas[n].size_type_id === this.sizeTypesSelect[i].value) {
                  this.sizeTypesSelect.splice(i, 1)
                }
              }
            }
          }).catch(response => {

          })
      },
      async searchQuotes() {
        this.selectAllTrigger = false
        this.total = null
        this.working = true
        let params = {
          page: 0,
          limit: 500,
          sort: [
            {
              key: 'id',
              direction: 'asc'
            }
          ],
          includes: [
            'quote_companies'
          ],
          filter_groups: []
        }
        params.filter_groups.push(
          {
            'filters': [
              ['type', 'eq', 'company']

            ]
          }
        )
        if (!this.$cookies.get('bypassCountry')) {
          params.filter_groups.push(
            {
              filters: [
                [this.$appConfig.models['quotes'].countrize, 'eq', window.country]
              ]
            }
          )
        }

        if (this.filters) {
          params.filter_groups.push(
            {filters: Object.values(this.filters)}
          )
        }
        await this.doSearchQuotes(params)
        this.working = false
      },
      async doSearchQuotes(params, prevQuotes = []) {
        let searchedQuotes = []
        return await this.$http.get(this.$appConfig.api.baseUrl + this.$appConfig.models['quotes'].endpoint, {params: params})
          .then(async response => {
            if (this.filter.quoteIncludes === false) {
              searchedQuotes = response.body.data.map(x => {
                if (this.filters.quoteIds && this.filters.quoteIds[2].length > 0) {
                  return x.quote_companies.length <= 1 ? x : false
                } else if (this.filters.quoteCompanies && this.filters.quoteCompanies[2] && this.filters.quoteCompanies[2].length === 1) {
                  return x.quote_companies.length === 1 ? x : false
                } else if (!this.filters.quoteCompanies || !this.filters.quoteCompanies[2] || this.filters.quoteCompanies[2].length === 0) {
                  return x.quote_companies.length === 0 ? x : false
                }
              }).filter(Boolean)
            } else {
              searchedQuotes = response.body.data
            }

            searchedQuotes = prevQuotes.concat(searchedQuotes)
            if (response.body.data.length !== 0) {
              params.page = params.page + 1
              await this.doSearchQuotes(params, searchedQuotes)
            } else {
              this.total = searchedQuotes.length
              this.searchedQuotes = searchedQuotes
            }
          })
      },
      resetQuoteSearch() {
        this.total = null
        this.resetting = true
        this.searchedQuotes = []
        this.filters = []
        this.fileImported = null
        Object.assign(this.$data.filter, this.$options.data.call(this).filter)
        setTimeout(function () {
          this.resetting = false
        }.bind(this), 500)
      },
      rowSelected(items) {
        this.selected = items
      },
      selectAll() {
        let quotesTable = this.$refs.quotesTable.$el
        let tableBody = quotesTable.getElementsByTagName('tbody')[0]
        let tableRows = tableBody.getElementsByTagName('tr')
        this.unselectAll()
        for (const row of tableRows) {
          row.click()
        }
      },
      unselectAll() {
        this.$refs.quotesTable.clearSelected()
      },
      onSearchCompanySelected(item) {
        this.filter.quoteCompanies.push({id: item.id, name: item.name})
        this.$refs.companyAc.searchQuery = ''
      },
      removeCompanyFromFilters(index) {
        this.filter.quoteCompanies.splice(index, 1)
      },
      backupSelected() {
        let quotesExport = []
        for (let i = 0; i < this.selected.length; i++) {
          let originalQuote = this.selected[i]
          if (originalQuote.quote_formula && originalQuote.quote_formula.quote_formula_value && originalQuote.quote_formula.quote_formula_value.values) {
            let entries = Object.entries(originalQuote.quote_formula.quote_formula_value.values)
            for (let n = 0; n < entries.length; n++) {
              let regexp = new RegExp('{(' + entries[n][0] + ')}', 'g')
              originalQuote.quote_formula.formula = originalQuote.quote_formula.formula.replace(regexp, '{' + entries[n][0] + ':' + entries[n][1] + '}')
              originalQuote.quote_formula.formula_courier_commission = originalQuote.quote_formula.formula_courier_commission.replace(regexp, '{' + entries[n][0] + ':' + entries[n][1] + '}')
              originalQuote.quote_formula.formula_courier_commission_tour = originalQuote.quote_formula.formula_courier_commission_tour.replace(regexp, '{' + entries[n][0] + ':' + entries[n][1] + '}')
            }
          }

          try {
            let theQuote = {...originalQuote, ...originalQuote.quote_formula, ...originalQuote.quote_formula.quote_formula_value}
            delete (theQuote.id)
            delete (theQuote.vehicle_type_id)
            delete (theQuote.company_id)
            delete (theQuote.user_company)
            delete (theQuote.quote_formula_id)
            delete (theQuote.created_at)
            delete (theQuote.updated_at)
            delete (theQuote.quote_formula)
            delete (theQuote.parameters)
            delete (theQuote.quote_formula_value_id)
            delete (theQuote.quote_formula_value)
            delete (theQuote.values)
            if (theQuote.quote_companies.length === 1) {
              theQuote.companies = theQuote.quote_companies[0].company_id
            }
            delete (theQuote.quote_companies)
            quotesExport.push(theQuote)
          } catch (e) {
            console.log(e)
          }
        }

        let filename = 'quotes_backup'
        Object.values(this.filters).forEach(filter => {
          if (filter.length > 0) {
            filename += '__' + filter.join('-')
          }
        })
        filename += '.csv'
        this.exportToCsv(filename, Papa.unparse(quotesExport))
        this.backuped = true
      },
      async deleteSelected() {
        if (
          confirm(this.$gettext('Are you sure you want to DELETE the selected rows?\nWARNING: THIS ACTION CANNOT BE UNDONE!'))
        ) {
          this.deleting = true
          this.count = 0
          let count = 0
          for (let i = 0; i < this.selected.length; i++) {
            let theQuote = this.selected[i]
            let quoteFormulaValueId = theQuote.quote_formula && theQuote.quote_formula.quote_formula_value_id ? theQuote.quote_formula.quote_formula_value_id : 9999
            try {
              await this.$http.delete(this.$appConfig.api.baseUrl + 'quote-formula-values/' + quoteFormulaValueId)
                .finally(response => {
                  this.$http.delete(this.$appConfig.api.baseUrl + 'quote-formulas/' + theQuote.quote_formula_id)
                    .finally(response => {
                      if (theQuote.quote_companies.length === 1) {
                        this.$http.post(this.$appConfig.api.baseUrl + 'quotes/' + theQuote.id + '/companies', [])
                          .finally(response => {
                            this.$http.delete(this.$appConfig.api.baseUrl + 'quotes/' + theQuote.id)
                              .finally(response => {
                                count++
                                this.count = count
                                if (count === this.selected.length) {
                                  this.deleting = false
                                  this.$router.push({ name: 'modelList', params: { type: 'quotes' }, query: { deleted: true } })
                                }
                              })
                          })
                      } else {
                        this.$http.delete(this.$appConfig.api.baseUrl + 'quotes/' + theQuote.id)
                          .finally(response => {
                            count++
                            this.count = count
                            if (count === this.selected.length) {
                              this.deleting = false
                              this.$router.push({ name: 'modelList', params: { type: 'quotes' }, query: { deleted: true } })
                            }
                          })
                      }
                    })
                })
            } catch (e) {
              console.log(e)
            }
          }
        }
      },
      exportToCsv(filename, csv) {
        let blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
        if (navigator.msSaveBlob) { // IE 10+
          navigator.msSaveBlob(blob, filename)
        } else {
          let link = document.createElement('a')
          if (link.download !== undefined) { // feature detection
            // Browsers that support HTML5 download attribute
            let url = URL.createObjectURL(blob)
            link.setAttribute('href', url)
            link.setAttribute('download', filename)
            link.style.visibility = 'hidden'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
          }
        }
      }
    }
  }
</script>
